import type { Timestamp } from 'firebase/firestore';
import type { ReactNode } from 'react';
import React from 'react';
import type { MessageChannel, ChannelMessage } from '../types/messaging';
import { CHANNEL_CONFIGS } from '../types/messaging';
import { TEMPLATE_DRAFTS } from './whatsappTemplateDrafts';

type TemplateEditPreview = {
  submitted?: Record<string, { name?: string; hash?: string }>;
};

/** Keep the Meta template identifier intact while presenting one naming scheme in Messages. */
export function getTemplateDisplayName(name: string | null | undefined): string {
  const raw = String(name || '').trim();
  if (!raw) return '';
  return `t2_${raw.replace(/^(?:swarna|t\d+)_/i, '')}`;
}

export function getMessageDisplayText(
  message: ChannelMessage,
  templateEdits: Record<string, TemplateEditPreview> = {}
): string {
  const text = String(message.text || '').trim();
  if (message.type !== 'template') return text;
  const name = String(message.templateName || '').trim();
  if (text && text !== `[${name}]` && !/^\[swarna[_a-z0-9]+\]$/i.test(text)) return text;

  const base = name.replace(/_v\d+$/, '');
  const draft = TEMPLATE_DRAFTS.find((item) => item.name === base);
  const language = String(message.templateLanguage || 'en').toLowerCase().split('_')[0];
  const lang = language === 'gu' || language === 'hi' ? language : 'en';
  let body = draft?.body[lang] || '';
  const submitted = templateEdits[base]?.submitted?.[lang];
  if (submitted?.name === name && submitted.hash) {
    try {
      const submittedContent = JSON.parse(submitted.hash);
      if (Array.isArray(submittedContent) && typeof submittedContent[0] === 'string') {
        body = submittedContent[0];
      }
    } catch {
      // Keep the built-in wording if an older submission has no readable hash.
    }
  }
  const bodyComponent = message.templateComponents?.find(
    (component: any) => String(component?.type || '').toLowerCase() === 'body'
  );
  const parameters = Array.isArray(bodyComponent?.parameters) ? bodyComponent.parameters : [];
  if (body) {
    return body.replace(/\{\{(\d+)\}\}/g, (token, position) => {
      const value = parameters[Number(position) - 1]?.text;
      return value == null ? token : String(value);
    });
  }
  return text || (name ? `Template: ${name}` : 'Template message');
}

export function getMessageTemplateButtons(
  message: ChannelMessage,
  templateEdits: Record<string, TemplateEditPreview> = {}
): string[] {
  if (message.type !== 'template') return [];
  if (Array.isArray(message.templateButtons) && message.templateButtons.length) {
    return message.templateButtons.filter((label) => typeof label === 'string' && label.trim());
  }

  const name = String(message.templateName || '').trim();
  const base = name.replace(/_v\d+$/, '');
  const draft = TEMPLATE_DRAFTS.find((item) => item.name === base);
  const language = String(message.templateLanguage || 'en').toLowerCase().split('_')[0];
  const lang = language === 'gu' || language === 'hi' ? language : 'en';
  let labels = draft?.buttons?.map((button) => button[lang]) || [];
  const submitted = templateEdits[base]?.submitted?.[lang];
  if (submitted?.name === name && submitted.hash) {
    try {
      const content = JSON.parse(submitted.hash);
      if (Array.isArray(content) && Array.isArray(content[2])) {
        labels = content[2].filter((label): label is string => typeof label === 'string');
      }
    } catch {
      // Keep the built-in labels if an older submission has no readable hash.
    }
  }
  return labels.filter((label) => label.trim());
}

export function getChannelConfig(channel: MessageChannel) {
  return CHANNEL_CONFIGS[channel] ?? CHANNEL_CONFIGS.whatsapp;
}

export function normalizeContactId(channel: MessageChannel, identifier: string): string {
  return `${channel}:${identifier}`;
}

export function parseContactId(contactId: string): { channel: MessageChannel; identifier: string } {
  const idx = contactId.indexOf(':');
  if (idx === -1) return { channel: 'whatsapp', identifier: contactId };
  return {
    channel: contactId.slice(0, idx) as MessageChannel,
    identifier: contactId.slice(idx + 1),
  };
}

export function normalizePhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

export function formatPhoneDisplay(phone: string): string {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) {
    return `+${d.slice(0, 2)} ${d.slice(2, 7)} ${d.slice(7)}`;
  }
  return `+${d}`;
}

export function formatBrokerDisplayName(brokerName: string, agencyName?: string | null): string {
  const broker = String(brokerName || '').trim();
  const agency = String(agencyName || '').trim();
  if (!broker) return agency;
  return agency ? `${broker} (${agency})` : broker;
}

export function formatMessageTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts as unknown as number);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  const timeStr = date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });

  if (diffDays === 0) return timeStr;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return date.toLocaleDateString('en-IN', { weekday: 'short' });
  }
  return date.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

export function formatBubbleTime(ts: Timestamp | null | undefined): string {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts as unknown as number);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export function formatChatDate(ts: Timestamp | null | undefined): string {
  if (!ts) return '';
  const date = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts as unknown as number);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata',
  });
}

export function formatUnsupportedMessageLabel(message: Pick<ChannelMessage, 'payload' | 'type'>): string {
  const payload = message.payload as any;
  const error = Array.isArray(payload?.errors) ? payload.errors[0] : null;
  const reason = String(error?.title || error?.message || payload?.unsupported?.type || '').trim();
  return reason ? `Unsupported WhatsApp message (${reason})` : 'Unsupported WhatsApp message';
}

type WhatsAppTextSegment = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  mono?: boolean;
};

const WHATSAPP_FORMAT_RE = /(```[\s\S]+?```|`[^`\n]+`|\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)/g;

function parseWhatsAppTextLine(line: string): WhatsAppTextSegment[] {
  const segments: WhatsAppTextSegment[] = [];
  let lastIndex = 0;
  line.replace(WHATSAPP_FORMAT_RE, (match, _token, offset) => {
    if (offset > lastIndex) {
      segments.push({ text: line.slice(lastIndex, offset) });
    }
    if (match.startsWith('```') && match.endsWith('```')) {
      segments.push({ text: match.slice(3, -3), mono: true });
    } else if (match.startsWith('`') && match.endsWith('`')) {
      segments.push({ text: match.slice(1, -1), mono: true });
    } else if (match.startsWith('*') && match.endsWith('*')) {
      segments.push({ text: match.slice(1, -1), bold: true });
    } else if (match.startsWith('_') && match.endsWith('_')) {
      segments.push({ text: match.slice(1, -1), italic: true });
    } else if (match.startsWith('~') && match.endsWith('~')) {
      segments.push({ text: match.slice(1, -1), strike: true });
    } else {
      segments.push({ text: match });
    }
    lastIndex = offset + match.length;
    return match;
  });
  if (lastIndex < line.length) {
    segments.push({ text: line.slice(lastIndex) });
  }
  return segments.length ? segments : [{ text: line }];
}

export function renderWhatsAppFormattedText(text: string): ReactNode {
  return String(text || '')
    .split('\n')
    .map((line, lineIdx, lines) => React.createElement(
      React.Fragment,
      { key: `wa_line_${lineIdx}` },
      ...parseWhatsAppTextLine(line).map((segment, segmentIdx) => React.createElement(
        'span',
        {
          key: `wa_segment_${lineIdx}_${segmentIdx}`,
          style: {
            fontWeight: segment.bold ? 700 : undefined,
            fontStyle: segment.italic ? 'italic' : undefined,
            textDecoration: segment.strike ? 'line-through' : undefined,
            fontFamily: segment.mono ? 'monospace' : undefined,
          },
        },
        segment.text,
      )),
      lineIdx < lines.length - 1 ? React.createElement('br', { key: `wa_break_${lineIdx}` }) : null,
    ));
}
